import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FaDownload, FaFileInvoiceDollar } from 'react-icons/fa';
import { toast } from 'react-toastify';
import moment from 'moment';
import ErpTemplate from '../../components/ErpTemplate';
import UploadDropzone from '../../components/UploadDropzone';
import api from '../../services/api';
import notify from '../../services/notify';
import '../../css/financeiro-parcela-pagar.css';

const API = process.env.REACT_APP_API_URL;

/**
 * Faturas mensais da CVC, subidas pelo Aéreo. A tela só guarda e lista: sem conferência do PDF
 * e sem exclusão — a fatura subida fica como registro de quem subiu e quando.
 */
export default function AereoFaturaCvc() {
    const token = localStorage.getItem('token');
    const [faturas, setFaturas] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [baixando, setBaixando] = useState(null);

    const carregar = useCallback(() => {
        setCarregando(true);
        api({ method: 'get', action: '/aereoFatura', token })
            .then(res => setFaturas(res.data.data ?? []))
            .catch(err => notify.apiError(err, 'Erro ao carregar as faturas'))
            .finally(() => setCarregando(false));
    }, [token]);

    useEffect(() => { carregar(); }, [carregar]);

    // Sobe na hora em que o arquivo é escolhido: não há o que conferir antes.
    const enviar = async (escolhidos) => {
        const arquivos = Array.isArray(escolhidos) ? escolhidos : [escolhidos];
        if (!arquivos.length) return;
        setEnviando(true);
        try {
            const fd = new FormData();
            arquivos.forEach(f => fd.append('arquivos[]', f));
            await axios.post(`${API}/aereoFatura`, fd, { headers: { Authorization: `Bearer ${token}` } });
            toast.success(arquivos.length === 1 ? 'Fatura enviada' : `${arquivos.length} faturas enviadas`);
            carregar();
        } catch (err) {
            notify.apiError(err, 'Erro ao enviar a fatura');
        } finally {
            setEnviando(false);
        }
    };

    const baixar = async (fatura) => {
        setBaixando(fatura.id);
        try {
            const res = await axios.get(`${API}/aereoFaturaArquivo`, {
                params: { id: fatura.id },
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob',
            });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(res.data);
            link.download = fatura.nome || 'fatura.pdf';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(link.href);
        } catch (err) {
            toast.error('Não foi possível baixar a fatura.');
        } finally {
            setBaixando(null);
        }
    };

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Faturas CVC</h1>
                        <p className="fin-pp__subtitle">Upload da fatura mensal da CVC pelo Aéreo.</p>
                    </div>
                </div>

                <div className="fin-pp__card mb-3" style={{ padding: 16 }}>
                    <UploadDropzone onChange={enviar} multiple centerOnDrag accept="application/pdf,.pdf"
                        title={enviando ? 'Enviando...' : 'Arraste o PDF da fatura ou clique para escolher'}
                        disabled={enviando} />
                </div>

                <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                    <div className="fin-pp__tblwrap">
                        <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                            <thead>
                                <tr>
                                    <th>Arquivo</th>
                                    <th>Enviada em</th>
                                    <th>Responsável</th>
                                    <th style={{ width: 1 }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {carregando ? (
                                    <tr><td colSpan={4} className="fin-pp__empty">Carregando...</td></tr>
                                ) : faturas.length === 0 ? (
                                    <tr><td colSpan={4} className="fin-pp__empty">Nenhuma fatura enviada.</td></tr>
                                ) : faturas.map(f => (
                                    <tr key={f.id}>
                                        <td className="fin-pp__cell-strong">
                                            <FaFileInvoiceDollar size={13} style={{ marginRight: 8, color: 'var(--fin-ac)' }} />
                                            {f.nome || 'fatura.pdf'}
                                        </td>
                                        <td>{f.enviadaEm ? moment(f.enviadaEm).format('DD/MM/YYYY HH:mm') : '—'}</td>
                                        <td>{f.responsavel || '—'}</td>
                                        <td>
                                            <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                                                disabled={baixando === f.id} onClick={() => baixar(f)}>
                                                <FaDownload size={11} />
                                                {baixando === f.id ? 'Baixando...' : 'Baixar'}
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </ErpTemplate>
    );
}
